import React from "react"

interface StateStat {
  _id: string
  count: number
}

interface StatsSummaryCardsProps {
  currentStats: StateStat[]
  previousStats: StateStat[]
}

const StatsSummaryCards: React.FC<StatsSummaryCardsProps> = ({ currentStats, previousStats }) => {
  const getTotalApplications = (stats: StateStat[] | undefined) => {
    if (!stats || !Array.isArray(stats)) {
      return 0
    }
    return stats.reduce((total, stat) => total + stat.count, 0)
  }

  const getUserSidePendingTotal = (stats: StateStat[] | undefined) => {
    if (!stats || !Array.isArray(stats)) {
      return 0
    }
    const userSideStates = [
      "invite_new",
      "waiting_address",
      "address_rejected",
      "waiting_content",
      "content_rejected",
      "ugc_rejected",
      "content_approved",
    ]
    return stats
      .filter((stat) => userSideStates.includes(stat._id))
      .reduce((total, stat) => total + stat.count, 0)
  }

  const getCompanySidePendingTotal = (stats: StateStat[] | undefined) => {
    if (!stats || !Array.isArray(stats)) {
      return 0
    }
    const companySideStates = [
      "invite_success",
      "first_application",
      "address_to_approve",
      "content_to_share",
      "content_shared",
      "ugc_content",
    ]
    return stats
      .filter((stat) => companySideStates.includes(stat._id))
      .reduce((total, stat) => total + stat.count, 0)
  }

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) {
      return current > 0 ? 100 : 0
    }
    return ((current - previous) / previous) * 100
  }

  const getChangeColor = (change: number) => {
    if (change > 0) {
      return "text-green-600"
    }
    if (change < 0) {
      return "text-red-600"
    }
    return "text-gray-600"
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-4 items-stretch">
      {/* Toplam Başvuru */}
      <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col justify-between h-full min-w-[220px] max-w-[340px] border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-base font-medium text-gray-700">Toplam Başvuru</span>
          <span className="bg-gray-100 rounded-lg p-2 text-gray-400 text-lg">📊</span>
        </div>
        <div className="text-3xl font-bold text-gray-900 mb-1">
          {getTotalApplications(currentStats).toLocaleString()}
        </div>
        <div className="flex items-center text-xs text-gray-500 mt-1">
          <span>Güncel durum</span>
          <span className="mx-1">·</span>
          <span className="text-gray-400">Önceki:</span>
          <span className="ml-1 text-gray-700">
            {getTotalApplications(previousStats).toLocaleString()}
          </span>
          <span
            className={`ml-1 ${getChangeColor(
              calculateChange(
                getTotalApplications(currentStats),
                getTotalApplications(previousStats),
              ),
            )}`}
          >
            (
            {calculateChange(
              getTotalApplications(currentStats),
              getTotalApplications(previousStats),
            ) > 0
              ? "+"
              : ""}
            {calculateChange(
              getTotalApplications(currentStats),
              getTotalApplications(previousStats),
            ).toFixed(1)}
            %)
          </span>
        </div>
      </div>

      {/* Kullanıcı Tarafı Bekleyen */}
      <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col justify-between h-full min-w-[220px] max-w-[340px] border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-base font-medium text-cyan-700">Kullanıcı Tarafı Bekleyen</span>
          <span className="bg-gray-100 rounded-lg p-2 text-cyan-400 text-lg">👤</span>
        </div>
        <div className="text-3xl font-bold text-gray-900 mb-1">
          {getUserSidePendingTotal(currentStats).toLocaleString()}
        </div>
        <div className="flex items-center text-xs text-gray-500 mt-1 flex-wrap">
          <span>Kullanıcı işlemleri bekleyen</span>
          <span className="mx-1">·</span>
          <span className="text-gray-400">Önceki:</span>
          <span className="ml-1 text-gray-700">{getUserSidePendingTotal(previousStats)}</span>
          <span
            className={`ml-1 ${getChangeColor(
              calculateChange(
                getUserSidePendingTotal(currentStats),
                getUserSidePendingTotal(previousStats),
              ),
            )}`}
          >
            (
            {calculateChange(
              getUserSidePendingTotal(currentStats),
              getUserSidePendingTotal(previousStats),
            ) > 0
              ? "+"
              : ""}
            {calculateChange(
              getUserSidePendingTotal(currentStats),
              getUserSidePendingTotal(previousStats),
            ).toFixed(1)}
            %)
          </span>
        </div>
      </div>

      {/* Firma Tarafı Bekleyen */}
      <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col justify-between h-full min-w-[220px] max-w-[340px] border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-base font-medium text-orange-700">Firma Tarafı Bekleyen</span>
          <span className="bg-gray-100 rounded-lg p-2 text-orange-400 text-lg">🏢</span>
        </div>
        <div className="text-3xl font-bold text-gray-900 mb-1">
          {getCompanySidePendingTotal(currentStats).toLocaleString()}
        </div>
        <div className="flex items-center text-xs text-gray-500 mt-1 flex-wrap">
          <span>Firma onayı bekleyen</span>
          <span className="mx-1">·</span>
          <span className="text-gray-400">Önceki:</span>
          <span className="ml-1 text-gray-700">{getCompanySidePendingTotal(previousStats)}</span>
          <span
            className={`ml-1 ${getChangeColor(
              calculateChange(
                getCompanySidePendingTotal(currentStats),
                getCompanySidePendingTotal(previousStats),
              ),
            )}`}
          >
            (
            {calculateChange(
              getCompanySidePendingTotal(currentStats),
              getCompanySidePendingTotal(previousStats),
            ) > 0
              ? "+"
              : ""}
            {calculateChange(
              getCompanySidePendingTotal(currentStats),
              getCompanySidePendingTotal(previousStats),
            ).toFixed(1)}
            %)
          </span>
        </div>
      </div>
    </div>
  )
}

export default StatsSummaryCards
